import React, { useEffect, useMemo, useState } from "react";
import {
  FaDownload,
  FaEnvelope,
  FaPhoneAlt,
  FaIdBadge,
  FaGraduationCap,
  FaBriefcase,
  FaCalendarAlt,
  FaBuilding,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { listInternshipApplicants } from "./api";
import { getBackendUrl } from "../utils/paths";
import styles from "./viewinternship.module.css";

const ITEMS_PER_PAGE = 5;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB");
};

const getDocumentUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const backendPath = normalizedPath.startsWith("/uploads/")
    ? `/backend${normalizedPath}`
    : normalizedPath;

  return getBackendUrl(backendPath);
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const second = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + second).toUpperCase();
};

// Deterministic pastel avatar color based on name, so the same
// applicant always gets the same avatar background.
const AVATAR_PALETTE = [
  { bg: "#ede9fe", color: "#7c3aed" }, // purple
  { bg: "#dbeafe", color: "#2563eb" }, // blue
  { bg: "#fce7f3", color: "#db2777" }, // pink
  { bg: "#d1fae5", color: "#059669" }, // green
  { bg: "#ffedd5", color: "#ea580c" }, // orange
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  const code = name.charCodeAt(0) + (name.charCodeAt(name.length - 1) || 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

export default function InternshipApplicants() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let active = true;

    listInternshipApplicants()
      .then((data) => {
        if (active) setApplicants(data.items || []);
      })
      .catch((err) => {
        if (active) setError(err.message || "Failed to load internship applicants");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(applicants.length / ITEMS_PER_PAGE));

  const pagedApplicants = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return applicants.slice(start, start + ITEMS_PER_PAGE);
  }, [applicants, page]);

  useEffect(() => {
    // keep the current page valid if the list shrinks/grows
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  if (loading) {
    return <div className={styles.pageState}>Loading internship applicants...</div>;
  }

  if (error) {
    return <div className={`${styles.pageState} ${styles.pageError}`}>Error: {error}</div>;
  }

  const rangeStart = applicants.length === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;
  const rangeEnd = Math.min(page * ITEMS_PER_PAGE, applicants.length);

  return (
    <div className={styles.page}>
      <div className={styles.headerBlock}>
        <h1 className={styles.pageTitle}>Internship Applicants</h1>
        <p className={styles.pageSubtitle}>
          {applicants.length} application{applicants.length === 1 ? "" : "s"} received
        </p>
      </div>

      {applicants.length === 0 ? (
        <div className={styles.empty}>No internship applications found.</div>
      ) : (
        <>
          <div className={styles.headerRow}>
            <div className={styles.headerCell}>
              <FaIdBadge /> Applicant Contact
            </div>
            <div className={styles.headerCell}>
              <FaGraduationCap /> Qualification
            </div>
            <div className={styles.headerCell}>
              <FaBriefcase /> Internship
            </div>
            <div className={styles.headerCell}>
              <FaCalendarAlt /> Dates
            </div>
            <div className={styles.headerCell}>
              <FaBuilding /> Institution
            </div>
            <div className={styles.headerCell}>Document</div>
            <div className={styles.headerCell}>Applied</div>
          </div>

          <div className={styles.cardList}>
            {pagedApplicants.map((applicant) => {
              const documentUrl = getDocumentUrl(applicant.document_path);
              const avatarColor = getAvatarColor(applicant.name);

              return (
                <div className={styles.card} key={applicant.id}>
                  {/* Applicant + Contact */}
                  <div className={`${styles.cell} ${styles.applicantCell}`}>
                    <div
                      className={styles.avatar}
                      style={{ backgroundColor: avatarColor.bg, color: avatarColor.color }}
                    >
                      {getInitials(applicant.name)}
                    </div>
                    <div>
                      <div className={styles.name}>{applicant.name || "-"}</div>
                      <div className={styles.email}>
                        <FaEnvelope className={styles.inlineIcon} />
                        {applicant.mail || "-"}
                      </div>
                      <div className={styles.subText}>
                        <FaPhoneAlt className={styles.inlineIcon} />
                        {applicant.phone || "-"}
                      </div>
                      <div className={styles.subText}>ID: {applicant.student_id || "-"}</div>
                    </div>
                  </div>

                  {/* Qualification */}
                  <div className={styles.cell}>
                    <div className={styles.badgeRow}>
                      <span className={`${styles.iconBox} ${styles.iconGreen}`}>
                        <FaGraduationCap />
                      </span>
                      <div>
                        <div className={styles.badgeTitle}>{applicant.qualification || "-"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Internship */}
                  <div className={styles.cell}>
                    <div className={styles.badgeRow}>
                      <span className={`${styles.iconBox} ${styles.iconPurple}`}>
                        <FaBriefcase />
                      </span>
                      <div>
                        <div className={styles.badgeTitle}>{applicant.internship || "-"}</div>
                        <div className={styles.badgeSubtitle}>
                          {applicant.type_of_internship || "-"} / {applicant.duration_of_internship || "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className={styles.cell}>
                    <div className={styles.badgeRow}>
                      <span className={`${styles.iconBox} ${styles.iconLavender}`}>
                        <FaCalendarAlt />
                      </span>
                      <div>
                        <div className={styles.badgeTitle}>{formatDate(applicant.start_date)}</div>
                        <div className={styles.badgeSubtitle}>to {formatDate(applicant.end_date)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Institution */}
                  <div className={styles.cell}>
                    <div className={styles.badgeRow}>
                      <span className={`${styles.iconBox} ${styles.iconOrange}`}>
                        <FaBuilding />
                      </span>
                      <div>
                        <div className={styles.badgeTitle}>{applicant.institution || "-"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Document */}
                  <div className={styles.cell}>
                    {documentUrl ? (
                      <a
                        className={styles.openBtn}
                        href={documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open uploaded document"
                      >
                        <FaDownload /> OPEN
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>

                  {/* Applied */}
                  <div className={styles.cell}>
                    <span className={styles.appliedPill}>
                      <FaCalendarAlt className={styles.inlineIcon} />
                      {formatDate(applicant.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.paginationBar}>
            <span className={styles.paginationLabel}>
              Showing {rangeStart} to {rangeEnd} of {applicants.length} applications
            </span>
            <div className={styles.paginationControls}>
              <button
                type="button"
                className={styles.pageArrow}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous page"
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  className={`${styles.pageNumber} ${num === page ? styles.pageNumberActive : ""}`}
                  onClick={() => setPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                type="button"
                className={styles.pageArrow}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                aria-label="Next page"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}